import { IMarketplaceProductVariant } from '../../types'
import { IIssueText } from '../../components/conditional-issue-text'
import { getMaxQuantity } from '../get-max-quantity'

export enum IssueType {
  OutOfStock = 'OUT_OF_STOCK',
  LowStock = 'LOW_STOCK',
  NotEnoughStock = 'NOT_ENOUGH_STOCK',
}

export enum IssueSeverity {
  Warning = 'WARNING',
  Error = 'ERROR',
}
export interface IIssue {
  type: IssueType
  severity: IssueSeverity
}
export interface IIssueWithMessage extends IIssue {
  message: string
}

export const OUT_OF_STOCK_MESSAGE = 'Out of stock'

const getIssueTypeWithMessage = (
  issues: IIssue[] = [],
  type: IssueType,
  message: string
): IIssueWithMessage | null => {
  const issue = (issues || []).find(iss => iss.type === type)
  if (issue) {
    return {
      ...issue,
      message,
    }
  }
  return null
}

const getLowStockPDPMessage = (maxQuantity: number): string =>
  `Low stock, ${maxQuantity} available`

export const getOutOfStockCartIssue = (): IIssueText => ({
  message: 'Out of stock, remove item to continue',
  severity: IssueSeverity.Error,
})

export const getOutOfStockCheckoutIssue = (): IIssueText => ({
  message: 'Out of stock, remove item in cart to continue',
  severity: IssueSeverity.Error,
})

export const getLowStockCartIssue = (
  maxQuantity: number,
  canFulfil: boolean
): IIssueText => ({
  message: canFulfil
    ? `Low stock, ${maxQuantity} available`
    : `${maxQuantity} available, reduce quantity to continue`,
  severity: canFulfil ? IssueSeverity.Warning : IssueSeverity.Error,
})

const getLowStockCheckoutIssue = (
  maxQuantity: number,
  canFulfil: boolean
): IIssueText => ({
  message: canFulfil
    ? `Low stock, ${maxQuantity} available`
    : `${maxQuantity} available, reduce quantity in cart to continue`,
  severity: canFulfil ? IssueSeverity.Warning : IssueSeverity.Error,
})

export const getCartItemIssueText = (
  issues: IIssue[],
  maxQuantity: number,
  isCartPage: boolean
): IIssueText | null => {
  if (!issues.length) {
    return null
  }
  if (issues[0].type === IssueType.OutOfStock) {
    return isCartPage ? getOutOfStockCartIssue() : getOutOfStockCheckoutIssue()
  }

  return isCartPage
    ? getLowStockCartIssue(maxQuantity, issues[0].type === IssueType.LowStock)
    : getLowStockCheckoutIssue(
        maxQuantity,
        issues[0].type === IssueType.LowStock
      )
}

const getMaxVariantQuantity = (
  variants: IMarketplaceProductVariant[]
): number =>
  variants.reduce(
    (maxQuantity, variant) => Math.max(maxQuantity, getMaxQuantity(variant)),
    0
  )

export const getProductStockIssue = (
  variants: IMarketplaceProductVariant[],
  selectedVariant: IMarketplaceProductVariant | null
): IIssueWithMessage | null => {
  const productVariants = selectedVariant ? [selectedVariant] : variants

  const outOfStockIssues = productVariants.flatMap(
    ({ issues }) =>
      getIssueTypeWithMessage(
        issues,
        IssueType.OutOfStock,
        OUT_OF_STOCK_MESSAGE
      ) ?? []
  )

  if (outOfStockIssues.length === productVariants.length) {
    return outOfStockIssues[0]
  }

  const lowStockIssue = getIssueTypeWithMessage(
    selectedVariant?.issues || [],
    IssueType.LowStock,
    getLowStockPDPMessage(getMaxVariantQuantity(productVariants))
  )

  if (lowStockIssue) {
    return lowStockIssue
  }

  return null
}

export const getProductListingIssue = (
  issues: IIssue[] = []
): IIssueWithMessage | null =>
  getIssueTypeWithMessage(issues, IssueType.OutOfStock, OUT_OF_STOCK_MESSAGE)
